import { getPasswordPolicy, PasswordPolicy } from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { PasswordPolicyError } from "@/config/types";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import { getPasswordInfo } from "../passwordUtils";

type UseChangePasswordDialogParams = {
  onClose: () => void;
  onSaveNewPassword: (
    oldPassword: string,
    newPassword: string,
  ) => Promise<void>;
  passwordRequirements: Record<PasswordPolicyError, boolean>;
  setPasswordRequirements: Dispatch<
    SetStateAction<Record<PasswordPolicyError, boolean>>
  >;
};

export function useChangePasswordDialog({
  onClose,
  onSaveNewPassword,
  passwordRequirements,
  setPasswordRequirements,
}: UseChangePasswordDialogParams) {
  const { lang } = useContext(PspContext);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy>();
  useEffect(() => {
    getPasswordPolicy().then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setPasswordPolicy(data);
      }
    });
  }, [lang]);

  const { passwordInvalid, passwordIsEmpty, passwordsDontMatch } =
    getPasswordInfo(passwordRequirements, newPassword, confirmNewPassword);

  function resetState() {
    setNewPassword("");
    setOldPassword("");
    setPasswordRequirements({
      missing_digit: true,
      missing_lowercase_letter: true,
      missing_symbol: true,
      missing_uppercase_letter: true,
      too_short: true,
    });
    setConfirmNewPassword("");
  }

  function onCloseWrapper() {
    onClose();
    setTimeout(resetState, 250);
  }

  function onSaveNewPasswordWrapper() {
    onSaveNewPassword(oldPassword, newPassword).then(resetState);
  }

  return {
    onCloseWrapper,
    onSaveNewPasswordWrapper,
    passwordsDontMatch,
    passwordIsEmpty,
    oldPassword,
    setOldPassword,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    lang,
    passwordPolicy,
    passwordInvalid,
  };
}
